import React, { useState } from 'react'
import { useRouter } from 'next/router'

import userService from 'services/user.service'
import ToastService from 'services/toast.service'

import { store } from 'redux/store'
import { useActionless, useAppSelector } from 'redux/hooks'
import { SET_COLLECTION_V2_STATE } from 'redux/collectionV2/actions/types'

import Link from 'components/elements/Link'
import Icon from 'components/elements/Icon'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import ExportModal from 'components/collectionV2/forms/ExportModal'
import ShareModal from 'components/collectionV2/forms/ShareModal'
import DeleteCollectionModal from 'components/collectionV2/forms/DeleteCollectionModal'
import EditLabelsModal from 'components/collectionV2/forms/EditLabelsModal'
import MultiEditControls from 'components/collectionV2/forms/multiEdit/Controls'

import styles from './header.module.scss'

type Props = {}

const HeaderControls = ({}: Props) => {
  const router = useRouter()
  const username = `${router.query.username}`

  const ownsCollection = useAppSelector(state => state.collectionV2.ownsCollection)

  const collectionIsPublic = useAppSelector(state => state.collectionV2.isPublic)

  const [setCollectionV2State] = useActionless(SET_COLLECTION_V2_STATE)

  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [editLabelsModalOpen, setEditLabelsModalOpen] = useState(false)

  const handleToggleCollectionPublicity = () => {
    userService
      .update({ profile: { is_collection_public: !collectionIsPublic } })
      .then(() => {
        ToastService.create('Collection status updated', 'Collection Page', 'success')
        setCollectionV2State({ isPublic: !collectionIsPublic })
      })
      .catch(() => ToastService.create('Unable to update collection status', 'Collection Page'))
  }

  const handleSelectAllCards = () => {
    const state = store.getState()
    const ids = Object.keys(state.collectionV2.collectionCards)

    setCollectionV2State({ multiSelectedIds: ids.reduce((a, id) => ({ ...a, [`${id}`]: true }), {}) })
  }

  return (
    <>
      <div className={styles.controlsAndOptions}>
        <div className={styles.left}>
          {ownsCollection && (
            <Link href="/collections/import">
              <Icon name="upload" /> Import
            </Link>
          )}

          <button onClick={() => setExportModalOpen(true)}>
            <Icon name="download" /> Export
          </button>
          <div className={styles.separator}>|</div>
          <button onClick={() => setShareModalOpen(true)}>
            <Icon name="share alternate" /> Share
          </button>
        </div>

        <div>
          {ownsCollection ? (
            <ArchidektDropdown
              menuClassName={styles.overflowMenu}
              options={[
                {
                  label: 'Create / edit collection labels',
                  icon: 'tags',
                  onClick: () => setEditLabelsModalOpen(true),
                },
                // { label: 'Cache settings', icon: 'cog', onClick: () => null },
                {
                  label: 'Select all',
                  icon: 'hand pointer outline',
                  onClick: handleSelectAllCards,
                  keybind: 'Ctrl + A',
                },
                { type: 'spacer' },
                { label: 'Collection data', icon: 'pie graph', to: `/collection/v2/data/${username}` },
                { type: 'spacer' },
                {
                  label: `Make collection ${collectionIsPublic ? 'private' : 'public'}`,
                  icon: collectionIsPublic ? 'lock' : 'lock open',
                  onClick: handleToggleCollectionPublicity,
                },
                { label: 'Delete collection', icon: 'trash', onClick: () => setDeleteModalOpen(true) },
                // Maybe in the future
                // { type: 'spacer' },
                // { label: 'Merge unique cards', icon: 'compress', onClick: () => null },
                // { label: 'Split unique cards', icon: 'expand', onClick: () => null },
              ]}>
              <button className={styles.overflowMenuTrigger}>
                <Icon name="cog" /> Extra options
              </button>
            </ArchidektDropdown>
          ) : (
            <Link href={`/collections/data/${username}/`}>
              <Icon name="pie graph" /> Collection data
            </Link>
          )}
        </div>
      </div>

      <DeleteCollectionModal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} />
      <ExportModal open={exportModalOpen} onClose={() => setExportModalOpen(false)} />
      <EditLabelsModal open={editLabelsModalOpen} onClose={() => setEditLabelsModalOpen(false)} />
      <ShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        collectionIsPublic={collectionIsPublic}
      />
      <MultiEditControls />
    </>
  )
}
export default HeaderControls
